<template>
  <b-dropdown
    ref="dropdown"
    block
    no-caret
    variant="link"
    class="main-select"
    toggle-class="main-select__toggle"
    menu-class="main-select__menu"
    :title="title"
    :disabled="disabled"
    @shown="onShown"
    @hidden="query = ''"
  >
    <template #button-content>
      <feather-icon
        v-if="icon"
        :icon="icon"
        size="18"
        class="main-select__icon"
      />
      <span
        class="main-select__label"
        :class="{ 'is-placeholder': !selectedText }"
      >
        {{ selectedText || placeholder || 'Select...' }}
      </span>
      <!-- reset to the default option (a span, because a button can't live inside the toggle button) -->
      <span
        v-if="showClear"
        role="button"
        class="main-select__clear"
        title="Clear"
        @click.stop.prevent="clear"
      >
        <feather-icon
          icon="XIcon"
          size="14"
        />
      </span>
      <feather-icon
        icon="ChevronDownIcon"
        size="16"
        class="main-select__chevron"
      />
    </template>

    <!-- search: filters the options while the user types -->
    <b-dropdown-form
      v-if="showSearch"
      class="main-select__search"
    >
      <div class="main-select__search-box">
        <feather-icon
          icon="SearchIcon"
          size="15"
        />
        <input
          ref="search"
          v-model="query"
          type="text"
          autocomplete="off"
          :placeholder="searchPlaceholder"
          @keydown.enter.prevent="selectFirst"
        >
        <button
          v-if="query"
          type="button"
          class="main-select__search-clear"
          title="Clear"
          @click="clearQuery"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
        </button>
      </div>
    </b-dropdown-form>

    <b-dropdown-item-button
      v-for="option in filtered"
      :key="String(option.value)"
      :active="option.value === value"
      @click="select(option.value)"
    >
      <span class="main-select__text">{{ option.text }}</span>
      <feather-icon
        v-if="option.value === value"
        icon="CheckIcon"
        size="16"
        class="main-select__check"
      />
    </b-dropdown-item-button>

    <b-dropdown-text
      v-if="!filtered.length"
      class="main-select__empty"
    >
      No results found
    </b-dropdown-text>
  </b-dropdown>
</template>

<script>
/**
 * MainSelect — shared dropdown select (styles live in
 * src/assets/scss/components/main-select.scss and are loaded globally by style.scss).
 *
 * <main-select
 *   v-model="filters.role"
 *   :options="[{ value: '', text: 'All roles' }, 'admin', 'user']"   // objects or plain values
 *   icon="BriefcaseIcon"          // optional feather icon
 *   placeholder="All Branches"    // shown when the value is empty (also enables the ✕ reset)
 *   title="Tooltip"               // optional
 *   clearable                     // ✕ button that resets to `clear-value` (default true)
 *   clear-value=""                // the default / "All" value the ✕ resets to
 *   searchable                    // search box in the menu (default true)
 *   :search-threshold="6"         // ...but only when there are at least this many options
 *   search-placeholder="Search..."
 *   disabled
 *   @change="onChange"            // fires after v-model has been updated
 * />
 */
export default {
  name: 'MainSelect',
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: [String, Number], default: '' },
    options: { type: Array, default: () => [] },
    icon: { type: String, default: null },
    // shown when the value is empty / matches no option ("Select..." when not given).
    // Giving one also tells the select that "empty" is a valid state, so the ✕ can reset to it.
    placeholder: { type: String, default: null },
    title: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    // ✕ inside the box: resets the select to `clearValue`
    clearable: { type: Boolean, default: true },
    // the default value ("All …") the ✕ button goes back to
    clearValue: { type: [String, Number], default: '' },
    searchable: { type: Boolean, default: true },
    // the search box only shows up when the list is at least this long
    searchThreshold: { type: Number, default: 6 },
    searchPlaceholder: { type: String, default: 'Search...' },
  },
  data() {
    return { query: '' }
  },
  computed: {
    normalized() {
      return this.options.map(o => (o !== null && typeof o === 'object' ? o : { value: o, text: String(o) }))
    },
    selectedText() {
      const found = this.normalized.find(o => o.value === this.value)
      return found ? found.text : ''
    },
    // only when a non-default value is picked AND there is something to go back to:
    // either the default exists as an option ("All …") or a `placeholder` was given.
    // Selects with neither (status, rows per page) never show a useless ✕.
    showClear() {
      return this.clearable
        && !this.disabled
        && this.value !== this.clearValue
        && (this.placeholder !== null || this.normalized.some(o => o.value === this.clearValue))
    },
    showSearch() {
      return this.searchable && this.normalized.length >= this.searchThreshold
    },
    filtered() {
      const term = this.query.trim().toLowerCase()
      if (!term) return this.normalized
      return this.normalized.filter(o => String(o.text).toLowerCase().includes(term))
    },
  },
  methods: {
    // menu opened: start typing right away
    onShown() {
      if (!this.showSearch) return
      this.$nextTick(() => {
        if (this.$refs.search) this.$refs.search.focus()
      })
    },
    clear() {
      this.select(this.clearValue)
    },
    clearQuery() {
      this.query = ''
      if (this.$refs.search) this.$refs.search.focus()
    },
    // Enter in the search box picks the first match
    selectFirst() {
      if (!this.filtered.length) return
      this.select(this.filtered[0].value)
      this.$refs.dropdown.hide(true)
    },
    select(value) {
      if (value === this.value) return
      this.$emit('input', value)
      this.$emit('change', value)
    },
  },
}
</script>
